<template>
  <div class="calculation-result" v-if="result">
    <!-- Share Distribution Table -->
    <div class="result-section">
      <h3 class="result-title">{{ $t('result.shareTable') }}</h3>

      <!-- Distributable summary (if estate provided) -->
      <div v-if="result.total_distributable" class="estate-summary">
        <div class="summary-row">
          <span>{{ $t('result.distributable') }}</span>
          <span class="summary-value">{{ formatCurrency(result.total_distributable) }}</span>
        </div>
        <div v-if="result.total_deductions" class="summary-row muted">
          <span>{{ $t('result.deductions') }}</span>
          <span>-{{ formatCurrency(result.total_deductions) }}</span>
        </div>
      </div>

      <!-- Shares table -->
      <div class="shares-table">
        <div class="shares-header">
          <span class="col-heir">{{ $t('result.heir') }}</span>
          <span class="col-count">{{ $t('result.count') }}</span>
          <span class="col-share">{{ $t('result.share') }}</span>
          <span class="col-per-person">{{ $t('result.perPerson') }}</span>
          <span v-if="hasAmounts" class="col-amount">{{ $t('result.amount') }}</span>
        </div>

        <div
          v-for="share in activeShares"
          :key="share.heir_category"
          class="shares-row"
        >
          <span class="col-heir">
            <span class="heir-name">{{ getHeirLabel(share.heir_category) }}</span>
            <span class="heir-type" :class="share.heir_type">
              {{ $t(`result.${share.heir_type}`) }}
            </span>
          </span>
          <span class="col-count">{{ share.heir_count }}</span>
          <span class="col-share">
            <span class="share-fraction" v-if="share.share_fraction_num">
              {{ share.share_fraction_num }}/{{ share.share_fraction_den }}
            </span>
            <span class="share-pct">{{ share.share_percentage.toFixed(2) }}%</span>
          </span>
          <span class="col-per-person">{{ share.per_person_pct.toFixed(2) }}%</span>
          <span v-if="hasAmounts" class="col-amount">
            <div v-if="share.amount_total">{{ formatCurrency(share.amount_total) }}</div>
            <div v-if="share.amount_per_person" class="per-person-amount">
              {{ formatCurrency(share.amount_per_person) }}/person
            </div>
          </span>
        </div>

        <!-- Total row -->
        <div class="shares-row total-row">
          <span class="col-heir">{{ $t('result.total') }}</span>
          <span class="col-count"></span>
          <span class="col-share">{{ totalPercentage.toFixed(2) }}%</span>
          <span class="col-per-person"></span>
          <span v-if="hasAmounts" class="col-amount">{{ formatCurrency(totalAmount) }}</span>
        </div>
      </div>
    </div>

    <!-- Visual Bar Chart -->
    <div class="result-section" v-if="activeShares.length > 0">
      <div class="bar-chart">
        <div
          v-for="share in activeShares"
          :key="'bar-' + share.heir_category"
          class="bar-row"
        >
          <span class="bar-label">{{ getHeirLabel(share.heir_category) }}</span>
          <div class="bar-track">
            <div
              class="bar-fill"
              :class="share.heir_type"
              :style="{ width: share.share_percentage + '%' }"
            ></div>
          </div>
          <span class="bar-value">{{ share.share_percentage.toFixed(1) }}%</span>
        </div>
      </div>
    </div>

    <!-- Awl / Radd Indicators -->
    <div v-if="result.awl_applied" class="result-notice warning">
      <strong>{{ $t('result.awlApplied') }}</strong>
      <p>{{ $t('result.awlNote', { denominator: result.awl_denominator }) }}</p>
    </div>

    <div v-if="result.radd_applied" class="result-notice info">
      <strong>{{ $t('result.raddApplied') }}</strong>
      <p>{{ $t('result.raddNote', { surplus: result.radd_surplus_pct?.toFixed(2) }) }}</p>
    </div>

    <!-- Blocked Heirs -->
    <div v-if="blockedShares.length > 0" class="result-section">
      <h4 class="blocked-title">{{ $t('result.blocked') }}</h4>
      <div
        v-for="share in blockedShares"
        :key="'blocked-' + share.heir_category"
        class="blocked-row"
      >
        <span class="blocked-heir">{{ getHeirLabel(share.heir_category) }}</span>
        <span class="blocked-by" v-if="share.blocked_by">
          {{ $t('result.blockedBy') }}: {{ getHeirLabel(share.blocked_by) }}
        </span>
      </div>
    </div>

    <!-- Calculation Steps -->
    <div v-if="result.steps?.length" class="result-section">
      <details class="steps-details">
        <summary class="steps-summary">
          {{ $t('result.steps') }} ({{ result.steps.length }})
        </summary>
        <ol class="steps-list">
          <li v-for="(step, i) in result.steps" :key="i" class="step-item">
            {{ step }}
          </li>
        </ol>
      </details>
    </div>

    <!-- Metadata -->
    <div class="result-meta">
      <span>{{ $t('result.schoolUsed') }}: {{ result.school }}</span>
      <span v-if="result.algorithm_version">{{ $t('result.algorithmVersion') }}: {{ result.algorithm_version }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  result: { type: Object, default: null },
})

// Map heir categories to i18n labels
const heirLabelMap = {
  husband: 'heir.husband',
  wife: 'heir.wife',
  son: 'heir.son',
  daughter: 'heir.daughter',
  father: 'heir.father',
  mother: 'heir.mother',
  sons_son: 'heir.sonsSon',
  sons_daughter: 'heir.sonsDaughter',
  fathers_father: 'heir.fathersFather',
  paternal_grandmother: 'heir.paternalGrandmother',
  maternal_grandmother: 'heir.maternalGrandmother',
  full_brother: 'heir.fullBrother',
  full_sister: 'heir.fullSister',
  paternal_half_brother: 'heir.paternalHalfBrother',
  paternal_half_sister: 'heir.paternalHalfSister',
  maternal_half_brother: 'heir.maternalHalfBrother',
  maternal_half_sister: 'heir.maternalHalfSister',
  full_brothers_son: 'heir.fullBrothersSon',
  paternal_half_brothers_son: 'heir.paternalHalfBrothersSon',
  full_paternal_uncle: 'heir.fullPaternalUncle',
  paternal_half_uncle: 'heir.paternalHalfUncle',
  full_cousin: 'heir.fullCousin',
  paternal_half_cousin: 'heir.paternalHalfCousin',
}

const getHeirLabel = (category) => {
  const key = heirLabelMap[category]
  return key ? t(key) : category.replace(/_/g, ' ')
}

const shares = computed(() => props.result?.shares || [])
const activeShares = computed(() => shares.value.filter((s) => !s.is_blocked))
const blockedShares = computed(() => shares.value.filter((s) => s.is_blocked))
const hasAmounts = computed(() => activeShares.value.some((s) => s.amount_total))
const totalPercentage = computed(() => activeShares.value.reduce((sum, s) => sum + s.share_percentage, 0))
const totalAmount = computed(() => activeShares.value.reduce((sum, s) => sum + (s.amount_total || 0), 0))

const formatCurrency = (value) => {
  if (!value && value !== 0) return ''
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value)
}
</script>

<style scoped>
.calculation-result {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.result-section {
  margin-bottom: var(--space-2);
}

.result-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4) 0;
}

/* Estate Summary */
.estate-summary {
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  margin-bottom: var(--space-4);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  padding: var(--space-1) 0;
}

.summary-row.muted {
  color: var(--color-gray-500);
}

.summary-value {
  font-weight: var(--font-weight-semibold);
}

/* Shares Table */
.shares-table {
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.shares-header {
  display: grid;
  grid-template-columns: 2fr 60px 120px 90px;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-gray-50);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.shares-header:has(.col-amount) {
  grid-template-columns: 2fr 60px 120px 90px 120px;
}

.shares-row {
  display: grid;
  grid-template-columns: 2fr 60px 120px 90px;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-top: 1px solid var(--color-gray-100);
  font-size: var(--font-size-sm);
  align-items: center;
}

.shares-row:has(.col-amount) {
  grid-template-columns: 2fr 60px 120px 90px 120px;
}

.total-row {
  background: var(--color-gray-50);
  font-weight: var(--font-weight-semibold);
}

.heir-name {
  display: block;
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.heir-type {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
}

.heir-type.quota { color: #2563eb; }
.heir-type.residuary { color: #059669; }
.heir-type.distant_kindred { color: #b45309; }

.share-fraction {
  display: block;
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.share-pct {
  font-weight: var(--font-weight-medium);
}

.col-count, .col-share, .col-per-person, .col-amount {
  text-align: right;
}

.per-person-amount {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

/* Bar Chart */
.bar-chart {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.bar-row {
  display: grid;
  grid-template-columns: 140px 1fr 60px;
  gap: var(--space-3);
  align-items: center;
}

.bar-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  text-align: end;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-track {
  height: 20px;
  background: var(--color-gray-100);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: var(--radius-sm);
  transition: width 0.5s ease-out;
  min-width: 2px;
}

.bar-fill.quota { background: #3b82f6; }
.bar-fill.residuary { background: #10b981; }
.bar-fill.distant_kindred { background: #f59e0b; }

.bar-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
  font-variant-numeric: tabular-nums;
}

/* Notices */
.result-notice {
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
}

.result-notice.warning {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  color: #92400e;
}

.result-notice.info {
  background: #dbeafe;
  border: 1px solid #3b82f6;
  color: #1e40af;
}

.result-notice p {
  margin: var(--space-1) 0 0 0;
  font-weight: normal;
}

/* Blocked Heirs */
.blocked-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  margin: 0 0 var(--space-2) 0;
}

.blocked-row {
  display: flex;
  justify-content: space-between;
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--color-gray-100);
  font-size: var(--font-size-sm);
}

.blocked-heir {
  color: var(--color-gray-400);
  text-decoration: line-through;
}

.blocked-by {
  color: var(--color-gray-500);
  font-size: var(--font-size-xs);
}

/* Steps */
.steps-details {
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
}

.steps-summary {
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  cursor: pointer;
}

.steps-summary:hover {
  color: var(--color-charcoal);
}

.steps-list {
  padding: 0 var(--space-4) var(--space-4);
  padding-inline-start: var(--space-8);
  margin: 0;
}

.step-item {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  padding: var(--space-1) 0;
  line-height: 1.5;
}

/* Meta */
.result-meta {
  display: flex;
  gap: var(--space-4);
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
}

/* Responsive */
@media (max-width: 640px) {
  .shares-header,
  .shares-row {
    grid-template-columns: 1.5fr 40px 80px 70px !important;
    font-size: var(--font-size-xs);
    padding: var(--space-2) var(--space-3);
  }

  .bar-row {
    grid-template-columns: 100px 1fr 50px;
  }
}
</style>
